import { ENVIRONMENT_INITIALIZER, inject, Injectable, Provider, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@core/services/auth/auth.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { PermissionsService } from '@core/services/permissions.service';
import { FeatureFlagsEnum } from '@generated/featureFlags.enum';
import { IaraCreditBalanceGQL } from '@generated/graphql';
import { PermissionsEnum } from '@generated/permissions.enum';
import { filter } from 'rxjs';
import type { IaraCreditBalance } from '../models/iara-credits.model';
import { loadInto } from '../utils/load-into.util';

/**
 * Fachada do saldo de créditos da Iara para o client. Carrega o saldo do ciclo vigente da
 * própria conta e expõe via signal. `null` quando a conta não tem créditos configurados (ou erro).
 *
 * Auto-bootstrap (via `IaraCreditsService.provider()` no `app.config`): reage ao usuário autenticado
 * e busca o saldo já na inicialização da aplicação — gateado por permissão (`global:iara_credits:read`)
 * + feature flag (`feature:chat-ai`) — para o widget do sidebar aparecer no boot, sem depender de
 * abrir a tela de Consumo de IA.
 */
@Injectable({ providedIn: 'root' })
export class IaraCreditsService {
  private readonly balanceGql = inject(IaraCreditBalanceGQL);
  private readonly authService = inject(AuthService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly featureFlagsService = inject(FeatureFlagService);

  public readonly balance = signal<IaraCreditBalance | null>(null);

  /** `true` enquanto o primeiro/atual fetch do saldo está em voo (para distinguir skeleton de vazio). */
  public readonly loading = signal(false);

  public constructor() {
    // Assim que o usuário autenticado (com permissões) está disponível, dispara o
    // carregamento gateado. `authenticatedUser$` reemite a cada (re)autenticação.
    this.authService.authenticatedUser$
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => this.loadIfPermitted());
  }

  /** Carrega o saldo (no-cache) e atualiza o signal. Falha silenciosamente (não bloqueia a UI). */
  public load(): void {
    loadInto(
      this.loading,
      this.balance,
      this.balanceGql.fetch({}, { fetchPolicy: 'no-cache' }),
      (result) => result.data?.iaraCreditBalance ?? null,
      null,
    );
  }

  /** Carrega o saldo apenas se a conta puder vê-lo (permissão) e a feature estiver ativa. */
  private loadIfPermitted(): void {
    if (!this.permissionsService.hasPermission(PermissionsEnum.GlobalIaraCreditsRead)) {
      return;
    }
    void this.featureFlagsService
      .isFeatureFlagEnabled(FeatureFlagsEnum.FeatureChatAi)
      .then((enabled) => {
        if (enabled) {
          this.load();
        }
      });
  }

  /**
   * Provider de inicialização: instancia o serviço no bootstrap da aplicação (`ENVIRONMENT_INITIALIZER`),
   * fazendo o auto-bootstrap do saldo rodar sem depender de nenhum componente injetar o serviço.
   */
  public static provider(): Provider {
    return {
      deps: [IaraCreditsService],
      multi: true,
      provide: ENVIRONMENT_INITIALIZER,
      useFactory: () => () => undefined,
    };
  }
}
